import { z } from 'zod';
import { ShopifyExtensionHandle } from '../../corso-shopify-extensions/shared.js';
import { DbJsonSchema } from '../../dbJsonSchemas.js';
import { InspectionGradeCode } from '../../enums/crew.js';
import { AppTypeEnum, BillingProviderEnum, CommissionPayeeTypeEnum, ContactTypeEnum, RoleCode, } from '../../enums/enum.js';
import { nullifyString } from '../../zodExtensions.js';
const productFeature = {
    Returns: 'Returns',
    Warranties: 'Warranties',
    Registrations: 'Registrations',
    ShippingProtection: 'Shipping Protection',
    Tracking: 'Tracking',
};
const fulfillmentLocation = z.object({
    id: z.number(),
    name: z.string().nullable(),
    allowReorder: z.boolean(),
});
const shippingLine = z.object({
    id: z.number(),
    name: z.string().nullable(),
    allowReorder: z.boolean(),
});
const storeContact = z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string(),
    email: z.string(),
    type: z.enum(ContactTypeEnum),
});
const storeUser = z.object({
    userId: z.number(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string(),
    roleCode: z.enum(RoleCode),
    createdOn: z.string(),
});
const store = z.object({
    id: z.number(),
    name: z.string(),
    myShopifyDomain: z.string(),
    enabledFeatures: z.array(z.enum(productFeature)),
    externalStorefrontId: z.string().nullable(),
    isAppInstalled: z.boolean(),
    defaultProtectSetId: z.number().nullable(),
    protectSetIds: z.array(z.number()),
    sharedSlackChannelId: z.string().nullable(),
    createdOn: z.string().nullable(),
    updatedOn: z.string().nullable(),
});
const invoice = z.object({
    id: z.number(),
    createdOn: z.string(),
    fromDate: z.string().nullable(),
    toDate: z.string().nullable(),
    totalAmount: z.number(),
    protectedOrderCount: z.number(),
    reorderCount: z.number(),
    refundCount: z.number(),
    currencyCode: z.string().nullable(),
    paidOn: z.string().nullable(),
    billingProvider: z.string().nullable(),
    commissionCalculations: z.array(z.object({
        id: z.number(),
        amount: z.number(),
        projectedAmount: z.number(),
        wasDeductedFromInvoice: z.boolean(),
        commissionPayeeName: z.string(),
    })),
    adjustments: z.array(z.object({
        id: z.number(),
        notes: z.string().nullable(),
        adjustmentType: z.string().nullable(),
        amount: z.number(),
        internalNotes: z.string().nullable(),
    })),
    sections: z.array(z.object({
        id: z.number(),
        name: z.string(),
        amount: z.number(),
        supportingDocumentPath: z.string().nullable(),
    })),
});
// ! currently these are admin settings in the corso merchant admin
const shippingProtectionSettings = z.object({
    isUnprotectedOrderClaimPermitted: z.boolean(),
});
const appType = z.union([
    z.literal(`${AppTypeEnum.crew}`),
    z.literal(`${AppTypeEnum.gsp}`),
]);
const stripeProvider = z.object({
    id: z.number(),
    provider: z.literal(`${BillingProviderEnum.stripe}`),
    email: z.string(),
    name: z.string(),
    stripeCustomerId: z.string(),
    appType,
});
const shopifyProvider = z.object({
    id: z.number(),
    provider: z.literal(`${BillingProviderEnum.shopifySubscription}`),
    appType,
});
const billingProvider = z.discriminatedUnion('provider', [
    stripeProvider,
    shopifyProvider,
]);
const billingProviderUpdate = z.discriminatedUnion('provider', [
    stripeProvider.omit({ appType: true, stripeCustomerId: true, id: true }),
    shopifyProvider.omit({ appType: true, id: true }),
]);
const commissionArrangement = z.object({
    id: z.number(),
    payeeName: z.string(),
    payeeType: z.string(),
    startDate: z.string(),
    endDate: z.string().nullable(),
    createdOn: z.string(),
    rate: z.number(),
    notes: z.string().nullable(),
    method: z.enum(['Topline', 'Traditional']),
    // deducted = isDeductedFromInvoice = true
    collectionMethod: z.enum(['Deducted', 'Postpaid']),
    isActive: z.boolean(),
});
const createCommissionArrangement = commissionArrangement
    .pick({
    method: true,
    collectionMethod: true,
    startDate: true,
    notes: true,
    rate: true,
})
    .extend({
    payeeName: z.string(),
    endDate: z.string().min(1, 'End Date is required'),
    payeeType: z.enum(CommissionPayeeTypeEnum),
});
const updateCommissionArrangement = createCommissionArrangement.pick({
    startDate: true,
    endDate: true,
});
const inspectionGrade = z.object({
    id: z.number(),
    name: z.string(),
    code: z.enum(InspectionGradeCode),
});
const invoiceDefinitionCreate = z.object({
    termInMonths: z.number(),
    name: z.string(),
    frequency: z.union([z.literal('Monthly'), z.literal('Weekly')]),
    startDate: z.string(),
    amount: z.number(),
    minimumAmount: z.number(),
    gspDiscount: z.number(),
});
const invoiceDefinitionCharge = z.object({
    id: z.number(),
    amount: z.number(),
    name: z.string(),
    type: z.enum({
        Billing_Period_Charge_Minimum: 'Billing_Period_Charge_Minimum',
        Billing_Period_Charge: 'Billing_Period_Charge',
        GSP_Discount: 'GSP_Discount',
    }),
});
const invoiceDefinition = z.object({
    id: z.number(),
    endDate: z.string().nullable(),
    startDate: z.string(),
    isUsed: z.boolean(),
    isTrial: z.boolean(),
    isActive: z.boolean(),
    frequency: z.union([z.literal('Monthly'), z.literal('Weekly')]),
    crewChargeMinimum: invoiceDefinitionCharge.nullable(),
    gspCharge: invoiceDefinitionCharge.nullable(),
    crewCharge: invoiceDefinitionCharge.nullable(),
    createdOn: z.string(),
});
const agency = z.object({
    id: z.number(),
    name: z.string(),
});
// heartbeats are recorded once per shopper page load, so recency reflects
// storefront traffic as much as extension health — the consumer decides severity
const storeExtension = z.object({
    appHandle: z.enum(ShopifyExtensionHandle),
    firstSeen: z.string().nullable(),
    lastSeen: z.string().nullable(),
    heartbeatCount: z.number(),
    locations: z.array(z.object({
        location: z.string(),
        firstSeen: z.string(),
        lastSeen: z.string(),
        heartbeatCount: z.number(),
    })),
});
const base = store.extend({
    shippingProtectionSettings,
    featureFlags: DbJsonSchema.Store.featureFlags,
    frontReportTags: DbJsonSchema.StoreSettings.frontReportTags,
    verifiedEmailDomain: nullifyString(z.string().toLowerCase().regex(z.regexes.domain, 'Must be a valid domain.')),
    agency: agency.nullable(),
    // TODO: canidates to move to their own get requests.
    commissionArrangements: z.array(commissionArrangement),
    billingProviders: z.array(billingProvider),
    inspectionGrades: z.array(inspectionGrade),
    invoiceDefinitions: z.array(invoiceDefinition),
    fulfillmentLocations: z.array(fulfillmentLocation),
    contacts: z.array(storeContact),
    shippingLines: z.array(shippingLine),
});
export const commandStore = {
    base,
    storeContact,
    user: storeUser,
    list: store,
    invoice,
    inspectionGrade,
    extension: storeExtension,
    commissionArrangement: {
        create: createCommissionArrangement,
        update: updateCommissionArrangement,
    },
    invoiceDefinition: {
        create: invoiceDefinitionCreate,
    },
    billingProvider: {
        update: billingProviderUpdate,
    },
};
//# sourceMappingURL=store.js.map